/**
 * Notification center for extension invites (Share copy + Add Users membership).
 * Renders a bell near the account block; Accept / Reject call /api/notifications.
 */

import { useCallback, useEffect, useState } from 'react'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import {
  acceptNotification,
  listNotifications,
  rejectNotification,
  type ExtensionNotification,
} from '../pages/extensionInvitesApi'

export interface NotificationsMenuProps {
  apiBaseUrl: string
  token: string | null
  collapsed: boolean
  onAccepted?: (result: { type: string; definitionId: string; displayName: string }) => void
}

export function NotificationsMenu({
  apiBaseUrl,
  token,
  collapsed,
  onAccepted,
}: NotificationsMenuProps) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const [items, setItems] = useState<ExtensionNotification[]>([])
  const [loading, setLoading] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!token && !apiBaseUrl) return
    setLoading(true)
    setError(null)
    try {
      const list = await listNotifications(apiBaseUrl, token)
      setItems(list)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [apiBaseUrl, token])

  useEffect(() => {
    void refresh()
    const id = window.setInterval(() => {
      void refresh()
    }, 15000)
    return () => window.clearInterval(id)
  }, [refresh])

  const open = Boolean(anchor)

  const handleAccept = async (n: ExtensionNotification) => {
    setBusyId(n.id)
    try {
      const result = await acceptNotification(apiBaseUrl, token, n.id)
      setItems((prev) => prev.filter((x) => x.id !== n.id))
      setAnchor(null)
      onAccepted?.(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyId(null)
    }
  }

  const handleReject = async (n: ExtensionNotification) => {
    setBusyId(n.id)
    try {
      await rejectNotification(apiBaseUrl, token, n.id)
      setItems((prev) => prev.filter((x) => x.id !== n.id))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <>
      <Tooltip title="Notifications" placement="right">
        <IconButton
          size="small"
          onClick={(e) => {
            setAnchor(e.currentTarget)
            void refresh()
          }}
          sx={{
            width: collapsed ? 40 : 36,
            height: collapsed ? 40 : 36,
            color: 'text.secondary',
            '&:hover': { color: 'primary.main', bgcolor: 'rgba(0,143,168,0.10)' },
          }}
          aria-label="Notifications"
        >
          <Badge
            color="error"
            badgeContent={items.length}
            max={9}
            invisible={items.length === 0}
          >
            <NotificationsNoneIcon sx={{ fontSize: collapsed ? 22 : 20 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 360, maxWidth: '92vw' } } }}
      >
        <Box sx={{ px: 2, pt: 1.25, pb: 1 }}>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 700 }}>Notifications</Typography>
          {error && (
            <Typography sx={{ fontSize: '0.75rem', color: 'error.main', mt: 0.5 }}>{error}</Typography>
          )}
        </Box>

        {loading && items.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={20} />
          </Box>
        ) : items.length === 0 ? (
          <Typography sx={{ px: 2, py: 2, fontSize: '0.82rem', color: 'text.secondary' }}>
            You are all caught up.
          </Typography>
        ) : (
          items.map((n) => (
            <Box
              key={n.id}
              sx={{
                px: 2,
                py: 1.25,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, mb: 0.25 }}>
                {n.title}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1 }}>
                {n.body}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={busyId === n.id}
                  onClick={() => void handleAccept(n)}
                >
                  Accept
                </Button>
                <Button
                  size="small"
                  disabled={busyId === n.id}
                  onClick={() => void handleReject(n)}
                >
                  Reject
                </Button>
              </Box>
            </Box>
          ))
        )}
      </Menu>
    </>
  )
}
